"use client";
import { useEffect, useId, useRef, useState } from "react";
import { siteCopy } from "./site";
import type { Link } from "./types";

/**
 * The menu for widths where the link row is hidden (below lg). It carries the same links
 * as the row, so there is one list to keep current, not two.
 */
export function NavDrawer({ links }: { links: Link[] }) {
  const [open, setOpen] = useState(false);
  const panelId = useId();
  const button = useRef<HTMLButtonElement>(null);

  // Escape closes and hands focus back, so the menu can be left without a pointer.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      setOpen(false);
      button.current?.focus();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  return (
    <div className="lg:hidden">
      <button
        ref={button}
        type="button"
        aria-expanded={open}
        aria-controls={panelId}
        aria-label={open ? siteCopy.menu.close : siteCopy.menu.open}
        onClick={() => setOpen((v) => !v)}
        className="grid size-8 cursor-pointer place-items-center rounded-full text-fg-muted transition-colors hover:text-fg"
      >
        <MenuIcon open={open} />
      </button>
      {/* Anchored to the sticky nav, which is a positioned ancestor. */}
      <div id={panelId} hidden={!open} className="absolute inset-x-0 top-full border-b border-line bg-bg">
        <ul className="wrap grid py-3">
          {links.map((l) => (
            <li key={l.href}>
              <a
                href={l.href}
                onClick={() => setOpen(false)}
                className="block py-2.5 text-sm text-fg-muted transition-colors hover:text-fg"
              >
                {l.label}
              </a>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

/** Two bars that cross into a close mark, so the button never changes meaning mid-animation. */
function MenuIcon({ open }: { open: boolean }) {
  return (
    <svg viewBox="0 0 16 16" aria-hidden className="size-4" fill="none" stroke="currentColor" strokeWidth={1.3}>
      <line
        x1="2.5"
        y1="5.5"
        x2="13.5"
        y2="5.5"
        className="origin-center transition-transform duration-200 ease-soft"
        style={open ? { transform: "translateY(2.5px) rotate(45deg)" } : undefined}
      />
      <line
        x1="2.5"
        y1="10.5"
        x2="13.5"
        y2="10.5"
        className="origin-center transition-transform duration-200 ease-soft"
        style={open ? { transform: "translateY(-2.5px) rotate(-45deg)" } : undefined}
      />
    </svg>
  );
}
